import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box, Button, Chip, Grid, LinearProgress, Stack, Typography,
} from "@mui/material";
import CourseLangLogo from "../components/CourseLangLogo";
import { DifficultyChip, EmptyState, PALETTE, SectionHeader, SectionPaper } from "../components/shared";
import type { LanguageTrack, TrackStep } from "../utils/languageTracks";

type Props = {
  tracks: LanguageTrack[];
  onOpenCourse?: (courseId: string) => void;
  onOpenTest?: (testId: string) => void;
  onViewTestSubmissions?: (testId: string) => void;
};

const STATUS_META: Record<TrackStep["status"], { label: string; color: string; bg: string }> = {
  completed: { label: "Completed", color: "#16a34a", bg: "#f0fdf4" },
  "in-progress": { label: "In Progress", color: "#2563eb", bg: "#eff6ff" },
  "not-started": { label: "Not Started", color: "#64748b", bg: "#f8fafc" },
};

function StepIcon({ step }: { step: TrackStep }) {
  if (step.status === "completed") return <CheckCircleIcon sx={{ fontSize: 18, color: "#16a34a" }} />;
  if (step.status === "in-progress") return <PlayCircleOutlineIcon sx={{ fontSize: 18, color: "#2563eb" }} />;
  return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: "#cbd5e1" }} />;
}

export default function TracksView({ tracks, onOpenCourse, onOpenTest, onViewTestSubmissions }: Props) {
  if (!tracks.length) return <EmptyState message="No learning roadmaps available yet. Publish courses and skill tests with a programming language to build tracks." />;

  return (
    <Grid container spacing={1.5}>
      {tracks.map((track) => (
        <Grid item xs={12} key={track.id}>
          <SectionPaper>
            <Box sx={{ px: 1.75, pt: 1.5, pb: 1.25, borderBottom: `1px solid ${PALETTE.border}` }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1.5}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <CourseLangLogo language={track.language} size={48} />
                  <Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: "primary.main", lineHeight: 1.2 }}>
                      {track.title}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary, mt: 0.25 }}>
                      {track.completedSteps} of {track.totalSteps} milestones completed
                    </Typography>
                  </Box>
                </Stack>
                <Stack alignItems="flex-end" spacing={0.5} sx={{ minWidth: 120 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: track.progressPercent >= 100 ? "success.main" : "primary.main" }}>
                    {track.progressPercent}%
                  </Typography>
                  <LinearProgress
                    variant="determinate"
                    value={track.progressPercent}
                    sx={{ width: 120, height: 8, borderRadius: 4 }}
                    color={track.progressPercent >= 100 ? "success" : "primary"}
                  />
                </Stack>
              </Stack>
            </Box>

            <Box sx={{ p: 1.75 }}>
              <Stack spacing={1.25}>
                {track.steps.map((step, index) => {
                  const meta = STATUS_META[step.status];
                  const isLast = index === track.steps.length - 1;
                  return (
                    <Stack key={step.id} direction="row" spacing={1.25}>
                      <Stack alignItems="center" sx={{ width: 24, flexShrink: 0 }}>
                        <StepIcon step={step} />
                        {!isLast && <Box sx={{ width: 2, flex: 1, minHeight: 28, bgcolor: "#e2e8f0", borderRadius: 1, mt: 0.5 }} />}
                      </Stack>

                      <Box
                        sx={{
                          flex: 1,
                          border: `1px solid ${PALETTE.border}`,
                          borderRadius: "12px",
                          p: 1.25,
                          bgcolor: step.status === "completed" ? "#fafdfa" : "#fff",
                        }}
                      >
                        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                          <Box sx={{ minWidth: 0 }}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5, flexWrap: "wrap" }}>
                              <Chip
                                size="small"
                                icon={step.type === "course" ? <AutoStoriesIcon sx={{ fontSize: "14px !important" }} /> : <QuizOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                                label={step.type === "course" ? "Course" : "Skill Test"}
                                sx={{ fontSize: 10, height: 22, bgcolor: step.type === "course" ? "#eff6ff" : "#f5f3ff", color: step.type === "course" ? "#1d4ed8" : "#6d28d9" }}
                              />
                              {step.difficulty && <DifficultyChip difficulty={step.difficulty} />}
                              <Chip size="small" label={meta.label} sx={{ fontSize: 10, height: 22, bgcolor: meta.bg, color: meta.color }} />
                            </Stack>
                            <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1f2937", mb: 0.25 }}>
                              {index + 1}. {step.title}
                            </Typography>
                            {(step.shortDescription || step.description) && (
                              <Typography sx={{ fontSize: 12, color: PALETTE.textSecondary, lineHeight: 1.45 }}>
                                {step.shortDescription || step.description}
                              </Typography>
                            )}
                            {step.type === "course" && (step.progressPercent ?? 0) > 0 && step.status !== "completed" && (
                              <Box sx={{ mt: 1 }}>
                                <LinearProgress variant="determinate" value={step.progressPercent ?? 0} sx={{ height: 6, borderRadius: 3 }} />
                                <Typography sx={{ fontSize: 11, color: PALETTE.textMuted, mt: 0.5 }}>{step.progressPercent}% course progress</Typography>
                              </Box>
                            )}
                          </Box>

                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Button
                              size="small"
                              variant={step.status === "completed" ? "outlined" : "contained"}
                              onClick={() => {
                                if (step.type === "course") {
                                  onOpenCourse?.(step.resourceId);
                                } else if (step.status === "completed") {
                                  onViewTestSubmissions?.(step.resourceId);
                                } else {
                                  onOpenTest?.(step.resourceId);
                                }
                              }}
                              sx={{ textTransform: "none", fontSize: 12, borderRadius: "8px", whiteSpace: "nowrap" }}
                            >
                              {step.status === "completed" ? "Review" : step.status === "in-progress" ? "Continue" : "Start"}
                            </Button>
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
          </SectionPaper>
        </Grid>
      ))}
    </Grid>
  );
}
